import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { RootLayout } from '@/components/layout/RootLayout'
import { GlassCard } from '@/components/ui/GlassCard'
import { GlassButton } from '@/components/ui/GlassButton'
import { GlassInput } from '@/components/ui/GlassInput'
import { orgQueryOptions } from '@/lib/queryOptions'
import { api } from '@/lib/api'
import { useT } from '@/i18n/provider'

const SECTOR_VALUES = ['general', 'healthcare', 'finance', 'legal', 'logistics', 'retail', 'education', 'manufacturing'] as const
const SECTOR_LABEL_KEYS = {
  general: 'settings.organization.sectors.general',
  healthcare: 'settings.organization.sectors.healthcare',
  finance: 'settings.organization.sectors.finance',
  legal: 'settings.organization.sectors.legal',
  logistics: 'settings.organization.sectors.logistics',
  retail: 'settings.organization.sectors.retail',
  education: 'settings.organization.sectors.education',
  manufacturing: 'settings.organization.sectors.manufacturing',
} as const

export const Route = createFileRoute('/settings/organization')({
  loader: async ({ context: { queryClient } }) => {
    await queryClient.ensureQueryData(orgQueryOptions()).catch(() => {})
  },
  component: OrganizationPage,
})

function OrganizationPage() {
  const t = useT()
  const qc = useQueryClient()
  const { data: org } = useQuery(orgQueryOptions())
  const [name, setName] = useState('')
  const [sector, setSector] = useState('')
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    if (org) {
      setName(org.name)
      setSector(org.sector)
    }
  }, [org])

  const updateMutation = useMutation({
    mutationFn: api.org.update,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org'] })
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    },
  })

  // No org yet — prompt to create
  const createMutation = useMutation({
    mutationFn: api.org.create,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['org'] }),
  })

  if (!org) {
    return (
      <RootLayout>
        <div className="space-y-6 max-w-2xl">
          <h1 className="text-xl font-bold text-white">{t('settings.organization.title')}</h1>
          <GlassCard className="space-y-4">
            <p className="text-white/60 text-sm">{t('settings.organization.notMember')}</p>
            <GlassInput
              label={t('settings.organization.organizationName')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('settings.organization.organizationNamePlaceholder')}
            />
            <GlassButton
              onClick={() => createMutation.mutate({ name, sector })}
              loading={createMutation.isPending}
              disabled={!name}
            >
              {t('settings.organization.createOrganization')}
            </GlassButton>
          </GlassCard>
        </div>
      </RootLayout>
    )
  }

  return (
    <RootLayout>
      <div className="space-y-6 max-w-2xl">
        <div>
          <h1 className="text-xl font-bold text-white">{t('settings.organization.title')}</h1>
          <p className="text-sm text-white/40 mt-0.5">{t('settings.organization.subtitle')}</p>
        </div>

        <GlassCard className="space-y-5">
          <GlassInput label={t('settings.organization.organizationName')} value={name} onChange={(e) => setName(e.target.value)} />

          <div>
            <label className="text-sm font-medium text-white/70 block mb-1.5">{t('settings.organization.sectorLabel')}</label>
            <select
              value={sector}
              onChange={(e) => setSector(e.target.value)}
              className="glass w-full px-4 py-2.5 text-white text-sm bg-transparent focus:outline-none focus:ring-2 focus:ring-[var(--oc-accent)]/60"
            >
              {SECTOR_VALUES.map((s) => (
                <option key={s} value={s} style={{ background: '#1a1a2e' }} className="capitalize">
                  {t(SECTOR_LABEL_KEYS[s])}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-3">
            <GlassButton
              onClick={() => updateMutation.mutate({ name, sector })}
              loading={updateMutation.isPending}
            >
              {t('common.saveChanges')}
            </GlassButton>
            {success && <span className="text-sm text-emerald-400">{t('settings.organization.saved')}</span>}
          </div>
        </GlassCard>

        <GlassCard size="sm">
          <p className="text-xs text-white/40 mb-1">{t('settings.organization.organizationId')}</p>
          <code className="text-xs text-white/60 font-mono">{org.id}</code>
          <p className="text-xs text-white/40 mt-3 mb-1">{t('settings.organization.plan')}</p>
          <p className="text-sm font-medium text-white capitalize">{org.plan.toLowerCase()}</p>
        </GlassCard>
      </div>
    </RootLayout>
  )
}
